// U13 (table fallback) + minimal scrubber for a FINISHED run: the playhead
// over Ui::Flow::RunTimelineComponent's attempt segments. The component
// renders the table, the bars and a role="slider" in the Timeline header;
// this controller owns the playhead position and nothing else — it never
// fetches, plays or animates. Moving the playhead (arrow keys, Home/End,
// PageUp/PageDown, pointer drag or programmatic seek()) emits
// `ui--flow-run-timeline:seek { ms, canvasId, activeSegments }` (bubbles) and
// the HOST decides what that time means for its canvas.
//
// Keys (WAI-ARIA slider): ←/↓ back, →/↑ forward by one step (Shift = ×10;
// step is 1% of the run, at least 1 ms), Home/End to the ends, PageUp/PageDown
// to the next/previous segment boundary. Motion: the playhead never
// transitions here — the CSS adds a short transition only under
// prefers-reduced-motion: no-preference, so reduced-motion users get an
// instant jump with no script involvement.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["slider", "segment", "readout"]

  static values = {
    // The canvas the host applies the time to — rides every seek event.
    canvasId: String,
    // Length of the axis; the playhead clamps to [0, durationMs].
    durationMs: { type: Number, default: 0 },
    // Current playhead position.
    positionMs: { type: Number, default: 0 },
    // Sorted segment start/end instants (PageUp/PageDown stops).
    boundaries: { type: Array, default: [] }
  }

  connect() {
    this._onPointerMove = this._onPointerMove.bind(this)
    this._onPointerUp = this._onPointerUp.bind(this)
    this._render()
  }

  disconnect() {
    this._endDrag()
  }

  // A late duration (the host extends a still-open run) re-bounds the axis.
  // Growing it moves nothing, so nothing is emitted. Shrinking it below the
  // playhead clamps the playhead — a real time change the host's canvas has
  // not seen — so that emits, like any other seek.
  durationMsValueChanged() {
    if (this._render()) this._emitSeek(this.positionMsValue)
  }

  // The host (or a Turbo/morph update) rewrote the position attribute: bring
  // the CSS variable, ARIA, readout and active marks along. No event for a
  // value that fits — the host that set it already knows the time it set. If
  // it had to be clamped (it no longer fits the axis) the time the host holds
  // is stale, so that emits. Stimulus flushes position and duration changes
  // in one batch, so BOTH callbacks must report a clamp, whichever runs first.
  // Our own seek() writes land here too, so this is idempotent.
  positionMsValueChanged() {
    if (this._render()) this._emitSeek(this.positionMsValue)
  }

  // Public API. Clamps to [0, durationMs] and rounds to a whole ms; a no-op
  // (same position) emits nothing, so holding End or dragging past the edge
  // does not spam the host.
  seek(ms) {
    const next = this._clamp(ms)
    if (next === this.positionMsValue) return
    this.positionMsValue = next
    this._render()
    this._emitSeek(next)
  }

  keydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey) return
    const step = this._step * (event.shiftKey ? 10 : 1)
    const now = this.positionMsValue
    let next = null
    switch (event.key) {
      case "ArrowRight":
      case "ArrowUp":
        next = now + step
        break
      case "ArrowLeft":
      case "ArrowDown":
        next = now - step
        break
      case "Home":
        next = 0
        break
      case "End":
        next = this.durationMsValue
        break
      case "PageUp":
        next = this._boundaryAfter(now)
        break
      case "PageDown":
        next = this._boundaryBefore(now)
        break
      default:
        return
    }
    event.preventDefault()
    this.seek(next)
  }

  pointerdown(event) {
    if (event.button !== undefined && event.button !== 0) return
    event.preventDefault()
    this.sliderTarget.focus()
    this._dragging = true
    this.seek(this._msAt(event.clientX))
    document.addEventListener("pointermove", this._onPointerMove)
    document.addEventListener("pointerup", this._onPointerUp)
    document.addEventListener("pointercancel", this._onPointerUp)
  }

  // Segments the playhead is inside: [start, end), plus two closing cases so
  // nothing is unreachable — an instant segment (start === end, a skipped or
  // point-in-time node run) is active exactly at its instant, and whatever
  // ends at the end of the run stays active at the very end.
  get activeSegments() {
    const ms = this.positionMsValue
    const end = this.durationMsValue
    return this.segmentTargets.filter((el) => {
      const s = Number(el.dataset.startMs)
      const e = Number(el.dataset.endMs)
      return ms >= s && (ms < e || (ms === e && (s === e || e === end)))
    })
  }

  get _step() { return Math.max(1, Math.round(this.durationMsValue / 100)) }

  _clamp(ms) {
    const n = Math.round(Number(ms))
    if (!Number.isFinite(n)) return this.positionMsValue
    return Math.min(Math.max(0, n), Math.max(0, this.durationMsValue))
  }

  _msAt(clientX) {
    const rect = this.sliderTarget.getBoundingClientRect()
    if (!rect.width) return this.positionMsValue
    return ((clientX - rect.left) / rect.width) * this.durationMsValue
  }

  _boundaryAfter(ms) {
    const next = this.boundariesValue.find((b) => b > ms)
    return next === undefined ? this.durationMsValue : next
  }

  _boundaryBefore(ms) {
    const prev = [...this.boundariesValue].reverse().find((b) => b < ms)
    return prev === undefined ? 0 : prev
  }

  _onPointerMove(event) {
    if (this._dragging) this.seek(this._msAt(event.clientX))
  }

  _onPointerUp() { this._endDrag() }

  _endDrag() {
    this._dragging = false
    document.removeEventListener("pointermove", this._onPointerMove)
    document.removeEventListener("pointerup", this._onPointerUp)
    document.removeEventListener("pointercancel", this._onPointerUp)
  }

  // One place that keeps the CSS variable, ARIA, readout and active-segment
  // marks in step with positionMs, so they cannot drift apart. Returns true
  // when the position had to be clamped into the axis.
  _render() {
    const duration = Math.max(0, this.durationMsValue)
    const ms = this._clamp(this.positionMsValue)
    const clamped = ms !== this.positionMsValue
    if (clamped) this.positionMsValue = ms
    const percent = duration === 0 ? 0 : (ms / duration) * 100
    this.element.style.setProperty("--ui-flow-rt-playhead", `${percent}%`)

    if (this.hasSliderTarget) {
      const text = `${this._format(ms)} / ${this._format(duration)}`
      this.sliderTarget.setAttribute("aria-valuemax", `${duration}`)
      this.sliderTarget.setAttribute("aria-valuenow", `${ms}`)
      this.sliderTarget.setAttribute("aria-valuetext", text)
    }
    if (this.hasReadoutTarget) this.readoutTarget.textContent = `${this._format(ms)} / ${this._format(duration)}`

    // Computed once per render and reused by the seek event, so a drag does
    // not scan and re-parse every segment twice per pointermove.
    this._active = this.activeSegments
    const active = new Set(this._active)
    this.segmentTargets.forEach((el) => {
      if (active.has(el)) el.setAttribute("data-active", "true")
      else el.removeAttribute("data-active")
    })
    return clamped
  }

  _format(ms) {
    return `${Math.round(ms).toLocaleString("en-US")} ms`
  }

  _emitSeek(ms) {
    const activeSegments = (this._active || this.activeSegments).map((el) => ({
      rowId: el.dataset.rowId,
      startMs: Number(el.dataset.startMs),
      endMs: Number(el.dataset.endMs),
      tone: el.dataset.tone,
      kind: el.dataset.kind
    }))
    this.element.dispatchEvent(new CustomEvent("ui--flow-run-timeline:seek", {
      detail: { ms, canvasId: this.canvasIdValue, activeSegments },
      bubbles: true
    }))
  }
}
